import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lifebuoy, Check, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { playHelpChime, unlockHelpChime } from "@/lib/helpChime";

const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const STAFF_ROLES = ["owner", "manager", "staff", "kitchen"];
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

// POS + KDS: kiosk "Get help" requests for this venue. Polls every 3 s (also catches up after a reconnect),
// chimes for new/unanswered requests at the owner's interval, Acknowledge and Complete are separate steps.
export function HelpAlerts({ role }) {
  const loc = useLocation();
  const nav = useNavigate();
  const kds = loc.pathname.startsWith("/kds");
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const lastChime = useRef(0);
  const seen = useRef(new Set());
  const allowed = STAFF_ROLES.includes(role);
  useEffect(() => {
    if (!allowed) return undefined;
    const unlock = () => unlockHelpChime();
    window.addEventListener("pointerdown", unlock, { once: true });
    let on = true;
    const run = () => api.get("/kiosk/help/active", { __skipAuthRedirect: true })
      .then((r) => { if (on) { setRows(r.data.help || []); setCfg(r.data.settings); } }).catch(() => {});
    run();
    const t = setInterval(run, 3000);
    return () => { on = false; clearInterval(t); window.removeEventListener("pointerdown", unlock); };
  }, [allowed]);
  const show = !!cfg && (kds ? cfg.kds : cfg.pos);
  const open = rows.filter((h) => h.status === "open");
  useEffect(() => {
    if (!show || !cfg.sound || !open.length) return;
    const fresh = open.some((h) => !seen.current.has(h.id));
    const due = cfg.repeat_seconds > 0 && Date.now() - lastChime.current >= cfg.repeat_seconds * 1000;
    open.forEach((h) => seen.current.add(h.id));
    if (fresh || due) { lastChime.current = Date.now(); playHelpChime(cfg.volume); }
  }, [rows, show]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!allowed || !show || !rows.length) return null;
  const act = async (h, what) => {
    try {
      await api.post(`/kiosk/help/${h.id}/${what}`);
      setRows((rs) => (what === "complete" ? rs.filter((x) => x.id !== h.id) : rs.map((x) => (x.id === h.id ? { ...x, status: "acknowledged" } : x))));
      toast.success(what === "ack" ? "Acknowledged — the customer has been told help is coming" : "Help request completed");
    } catch (e) { toast.error(errText(e)); }
  };
  const big = cfg.visual;
  return (
    <div className={`fixed z-[95] ${big ? "top-4 left-1/2 -translate-x-1/2 w-[min(560px,94vw)]" : "bottom-4 right-4 w-80"} space-y-2`} data-testid="help-alerts">
      {rows.slice(0, 4).map((h) => (
        <div key={h.id} data-testid={`help-alert-${h.id}`} data-status={h.status}
          className={`rounded-xl shadow-2xl border-2 ${h.status === "open" ? "bg-[#fff4ec] border-[#e2572b] help-alert-ring" : "bg-white border-[#94a3b8]"} p-4`}>
          <div className="flex items-start gap-3">
            <Lifebuoy size={big ? 30 : 22} weight="fill" className="text-[#e2572b] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className={`${big ? "text-lg" : "text-sm"} font-bold tracking-wide`}>{kds ? "AI KIOSK NEEDS HELP" : "CUSTOMER NEEDS HELP"}</div>
              <div className="text-sm text-[#475569]">{kds ? "Customer assistance requested." : "Customer waiting at AI Kiosk."}</div>
              <div className="text-xs text-[#475569] mt-1" data-testid={`help-alert-meta-${h.id}`}>
                {h.device_name} · {hhmm(h.created_at)}{h.reminders ? ` · pressed again ×${h.reminders}` : ""}
                {!kds && h.order_number ? ` · Order #${h.order_number}` : ""}
              </div>
              {!kds && h.reason && <div className="text-xs mt-0.5">{h.reason}</div>}
              {!kds && h.payment_attention && <div className="text-xs mt-1 font-semibold text-[#b45309]" data-testid={`help-alert-payment-${h.id}`}>Payment: {h.payment_attention}</div>}
              {h.status === "acknowledged" && <div className="text-xs mt-1 text-[#15803d]">Acknowledged by {h.acknowledged_by_name || "staff"} at {hhmm(h.acknowledged_at)}</div>}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {h.status === "open" && <button className="flex-1 h-10 rounded-lg bg-[#e2572b] text-white font-semibold inline-flex items-center justify-center gap-1" data-testid={`help-ack-${h.id}`} onClick={() => act(h, "ack")}><Check size={16} weight="bold" /> Acknowledge</button>}
            {!kds && <button className="flex-1 h-10 rounded-lg border font-semibold" data-testid={`help-view-${h.id}`} onClick={() => nav(h.order_number ? "/orders" : "/pos")}>View / assist order</button>}
            {h.status === "acknowledged" && <button className="flex-1 h-10 rounded-lg bg-[#0f2a3d] text-white font-semibold inline-flex items-center justify-center gap-1" data-testid={`help-complete-${h.id}`} onClick={() => act(h, "complete")}><CheckCircle size={16} /> Complete</button>}
          </div>
        </div>
      ))}
    </div>
  );
}
